import {
  AppointmentTimelineEvent,
  DocumentTimelineEvent,
  EntityType,
  EntryType,
  InteractionDirection,
  InteractionTimelineEvent,
  TimelineEvent,
} from '@maximizer/core/shared/domain';
import {
  getFontAwesomeIcon,
  InteractionIcons,
} from '@maximizer/core/shared/ui/icons';
import { parseISO } from 'date-fns';
import { AiMarkupUtils } from '../../classes/ai-markup-utils';
import { Helper } from '../../classes/iqboost';
import {
  DATA_ATTRIBUTES,
  isIgnoredReferenceType,
  readEntityKey,
  WITH_ENTITY_KEY_PARAM,
} from '../../pipes/ref-link.utils';

/** A `(Maximizer: { ... })` citation as the answer wrote it. */
export interface CrmCitation {
  key: string;
  /** Lower-cased; the prompt allows the six entity types, older answers wrote others. */
  type: string;
  /** The record's date as the answer wrote it, shown until the record itself is read. */
  date?: string;
  withKey?: string;
  withType?: EntryType;
  interactionDirection?: string;
  interactionType?: string;
  interactionExternal?: string;
}

/** What the CRM says about a cited record: the words its chip and card show. */
export interface CrmCitationRecord {
  key: string;
  type: EntityType;
  subject: string;
  date: Date;
  /** Appointments and tasks with a time of day show it; everything else shows the date alone. */
  hasTime: boolean;
  /** The entry the record is for, as the CRM displays it. */
  with?: string;
  description?: string;
  /** The interaction's own type, "Phone call" or "Meeting", for interaction records. */
  interaction?: string;
  direction?: InteractionDirection;
  file?: string;
  creator?: string;
}

const MAX_DESCRIPTION_LENGTH = 280;
const MAX_FALLBACK_SUBJECT_LENGTH = 80;

const BLOCK_BREAK_PATTERN =
  /<\/(?:p|div|li|h[1-6]|tr|td|th|blockquote|pre)>|<br\s*\/?>/gi;

const TYPE_ICONS: Record<string, string> = {
  appointment: getFontAwesomeIcon('calendar', 'solid'),
  task: getFontAwesomeIcon('circle-check', 'solid'),
  note: getFontAwesomeIcon('note-sticky', 'solid'),
  email: getFontAwesomeIcon('envelope', 'solid'),
  document: getFontAwesomeIcon('file-lines', 'solid'),
  interaction: getFontAwesomeIcon('comments', 'solid'),
};

const DEFAULT_ICON = getFontAwesomeIcon('arrow-up-right-from-square', 'solid');

const optional = (value: string | undefined): string | undefined => {
  const decoded = AiMarkupUtils.decodeMarkupEscaping(value ?? '').trim();
  return decoded || undefined;
};

/**
 * Reads a citation from its parsed parameters. Null for one with no key or type, and for an
 * investment or insurance record with nothing to open; each is erased, as in the classic chat.
 */
export function readCrmCitation(
  params: Record<string, string>,
): CrmCitation | null {
  const key = optional(readEntityKey(params));
  const type = optional(params['type'])?.toLowerCase();

  if (!key || !type || isIgnoredReferenceType(type)) {
    return null;
  }

  return {
    key,
    type,
    date: optional(params['date']),
    withKey: optional(params[WITH_ENTITY_KEY_PARAM]),
    withType: optional(params['withType']) as EntryType | undefined,
    interactionDirection: optional(params['interactionDirection']),
    interactionType: optional(params['interactionType']),
    interactionExternal: optional(params['interactionExternal']),
  };
}

/** The citation a rendered chip carries in its data attributes. */
export function readCrmCitationFromElement(
  element: HTMLElement,
): CrmCitation | null {
  const read = (name: string): string | undefined =>
    element.getAttribute(name)?.trim() || undefined;
  const key = read(DATA_ATTRIBUTES.KEY);
  const type = read(DATA_ATTRIBUTES.TYPE)?.toLowerCase();

  if (!key || !type) {
    return null;
  }

  return {
    key,
    type,
    date: read(DATA_ATTRIBUTES.DATE),
    withKey: read(DATA_ATTRIBUTES.WITH_ID),
    withType: read(DATA_ATTRIBUTES.WITH_TYPE) as EntryType | undefined,
    interactionDirection: read(DATA_ATTRIBUTES.INTERACTION_DIRECTION),
    interactionType: read(DATA_ATTRIBUTES.INTERACTION_TYPE),
    interactionExternal: read(DATA_ATTRIBUTES.INTERACTION_EXTERNAL),
  };
}

/** The distinct record keys an answer cites, in a stable order. */
export function collectCrmCitationKeys(
  markdown: string | null | undefined,
): string[] {
  if (!markdown?.includes('Maximizer:')) {
    return [];
  }

  const keys = new Set<string>();
  AiMarkupUtils.replaceMaximizerReferences(markdown, (params) => {
    const citation = readCrmCitation(params);
    if (citation) {
      keys.add(citation.key);
    }
    return null;
  });

  return [...keys].sort((a, b) => a.localeCompare(b));
}

/** The icon of a record's type; an interaction takes the icon of its own interaction type. */
export function crmCitationIcon(citation: CrmCitation): string {
  if (citation.type === 'interaction' && citation.interactionType) {
    const icon = InteractionIcons[citation.interactionType];
    // Sprite and Maximizer-font icons of the core app are not loaded here.
    if (icon?.startsWith('fa-icons')) {
      return icon;
    }
  }

  return crmRecordIcon(citation.type);
}

/** The icon of a record type, "note" or "appointment", as the chips and the action cards draw it. */
export function crmRecordIcon(type: string): string {
  return TYPE_ICONS[type] ?? DEFAULT_ICON;
}

/** The translation key of a record type's name, "Note" or "Meeting". */
export function crmCitationTypeKey(type: string): string {
  return (
    Helper.getCRMActionFromVariation(type)?.result.typeTranslationKey ??
    'types.entity'
  );
}

/** The words a chip shows for a record the CRM returned. */
export function toCrmCitationRecord(event: TimelineEvent): CrmCitationRecord {
  const description = plainText(event.description);
  const file =
    event.type === 'document'
      ? (event as DocumentTimelineEvent).file || undefined
      : undefined;
  const interaction =
    event.type === 'interaction'
      ? (event as InteractionTimelineEvent)
      : undefined;

  return {
    key: event.key ?? '',
    type: event.type,
    subject:
      event.subject?.trim() ||
      file ||
      interaction?.interaction?.display ||
      firstLine(description),
    date: toDate(event.date.value),
    hasTime: Boolean((event as AppointmentTimelineEvent).hasTime),
    with: event.with?.display || event.regarding?.display || undefined,
    description: description || undefined,
    interaction: interaction?.interaction?.display || undefined,
    direction: interaction?.direction,
    file,
    creator: event.creator?.display || undefined,
  };
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : parseISO(String(value));
}

// Notes and emails arrive as HTML when they were written rich; the card shows words only.
function plainText(value: string | undefined): string {
  if (!value) {
    return '';
  }

  // Block ends and line breaks become spaces first, or two paragraphs run into one word.
  const text = /<[a-z!/]/i.test(value)
    ? (new DOMParser().parseFromString(
        value.replace(BLOCK_BREAK_PATTERN, ' '),
        'text/html',
      ).body.textContent ?? '')
    : value;
  const collapsed = text.replace(/\s+/g, ' ').trim();

  return collapsed.length > MAX_DESCRIPTION_LENGTH
    ? `${collapsed.slice(0, MAX_DESCRIPTION_LENGTH - 1).trimEnd()}…`
    : collapsed;
}

function firstLine(text: string): string {
  const line = text.split(/[.!?](?:\s|$)/, 1)[0].trim();

  return line.length > MAX_FALLBACK_SUBJECT_LENGTH
    ? `${line.slice(0, MAX_FALLBACK_SUBJECT_LENGTH - 1).trimEnd()}…`
    : line;
}
